/**
 * CreateDraftModal
 *
 * Asks the user whether to increment the minor or major version
 * before creating a new draft. When a rollback has occurred (live
 * version differs from the highest version), also lets the user
 * choose which version to base the draft on.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import { incrementMinor, incrementMajor } from '../utils/versionUtils';

interface SourceVersion {
  id: string;
  versionNumber: string;
}

interface CreateDraftModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentVersion: string;
  type: 'taxonomy' | 'prompt';
  onConfirm: (versionNumber: string, sourceVersionId?: string) => void;
  isCreating?: boolean;
  /** The live version — when different from currentVersion, a source selector is shown. */
  liveSource?: SourceVersion;
  /** The latest (highest) version — shown as an alternative source after rollback. */
  latestSource?: SourceVersion;
}

const CreateDraftModal: React.FC<CreateDraftModalProps> = ({
  open,
  onOpenChange,
  currentVersion,
  type,
  onConfirm,
  isCreating = false,
  liveSource,
  latestSource,
}) => {
  const minorVersion = incrementMinor(currentVersion);
  const majorVersion = incrementMajor(currentVersion);
  const [selected, setSelected] = useState<'minor' | 'major'>('minor');
  const [source, setSource] = useState<'live' | 'latest'>('live');

  const showSourceSelector =
    liveSource && latestSource &&
    liveSource.versionNumber !== latestSource.versionNumber;

  const handleConfirm = () => {
    const versionNumber = selected === 'minor' ? minorVersion : majorVersion;
    const sourceVersionId = showSourceSelector && source === 'latest'
      ? latestSource!.id
      : undefined;
    onConfirm(versionNumber, sourceVersionId);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelected('minor');
      setSource('live');
    }
    onOpenChange(nextOpen);
  };

  const liveLabel = liveSource?.versionNumber ?? currentVersion;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Create Draft</DialogTitle>
          <DialogDescription>
            Create a new {type} draft. Choose the version number for the new draft.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Source version selector — only shown after rollback */}
          {showSourceSelector && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Base Version</Label>
              <RadioGroup value={source} onValueChange={(v) => setSource(v as 'live' | 'latest')}>
                <label
                  htmlFor="source-live"
                  className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                    source === 'live' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <RadioGroupItem value="live" id="source-live" />
                  <div className="flex-1 flex items-center justify-between">
                    <span className="text-sm font-medium">Live — v{liveSource!.versionNumber}</span>
                    <span className="text-xs text-gray-500">Current live version</span>
                  </div>
                </label>
                <label
                  htmlFor="source-latest"
                  className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                    source === 'latest' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <RadioGroupItem value="latest" id="source-latest" />
                  <div className="flex-1 flex items-center justify-between">
                    <span className="text-sm font-medium">Latest — v{latestSource!.versionNumber}</span>
                    <span className="text-xs text-gray-500">Most recent version</span>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* Version number selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Version Number</Label>
            <RadioGroup value={selected} onValueChange={(v) => setSelected(v as 'minor' | 'major')}>
              <label
                htmlFor="version-minor"
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  selected === 'minor' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <RadioGroupItem value="minor" id="version-minor" />
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm font-medium">Minor — v{minorVersion}</span>
                  <span className="text-xs text-gray-500">Patch / small change</span>
                </div>
              </label>

              <label
                htmlFor="version-major"
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  selected === 'major' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <RadioGroupItem value="major" id="version-major" />
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm font-medium">Major — v{majorVersion}</span>
                  <span className="text-xs text-gray-500">Significant change</span>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isCreating}>
            {isCreating ? 'Creating...' : `Create v${selected === 'minor' ? minorVersion : majorVersion}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDraftModal;
